import { useCallback, useMemo, useState } from "react";

import { Button } from "antd";
import ModalHistory from "../modal/history";
import { getHistory } from "../../../services/history";
import { isEmpty } from "../../../utils/common";
import { useTranslation } from "react-i18next";

const defaultTableData = {
  loading: false,
  dataSource: [],
  pagination: { current: 1, pageSize: 10, total: 0 },
};

const BtnHistory = ({ titleRef, object_id, content_type_id }) => {
  const { t } = useTranslation(["buttons", "history"]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableData, setTableData] = useState(defaultTableData);

  const title = useMemo(() => {
    return t("history:Title_History", { title: titleRef });
  }, [titleRef, t]);

  const searchHistory = useCallback(
    async (pageLimit = {}) => {
      if (!object_id || !content_type_id) return;

      const page = pageLimit.page ?? 1;
      const limit = pageLimit.limit ?? 10;

      try {
        setTableData((prev) => ({ ...prev, loading: true }));

        const params = {
          object_id,
          content_type_id,
          limit,
          page,
        };

        const resp = await getHistory(params);

        const dataSource = (resp.query ?? []).map((item) => {
          const msgList = [];

          if (!isEmpty(item.change_message)) {
            const parsedMessage = JSON.parse(item.change_message);

            if (Array.isArray(parsedMessage)) {
              parsedMessage.forEach((log) => {
                const getFieldLabel = (name) => (name ? t(`history:fields.${name}`, { defaultValue: name }) : "");
                const buildChangedFromToMsg = (raw, fieldName) => {
                  const fieldLabel = getFieldLabel(fieldName);
                  const match = raw.match(/from\s+"([^"]*)"\s+to\s+"([^"]*)"/i);

                  if (match) {
                    const [, from, to] = match;
                    return t("history:changed_from_to", {
                      field: fieldLabel || fieldName,
                      from,
                      to,
                    });
                  }
                  return t("history:changed_detail", {
                    field: fieldLabel || fieldName,
                    detail: raw,
                  });
                };

                // ---------- ADDED ----------
                if (log.added) {
                  const obj = log.added;
                  const fieldLabel = getFieldLabel(obj.name);
                  const detail = obj.object;

                  if (!fieldLabel && !detail) {
                    msgList.push(t("history:added"));
                  } else {
                    msgList.push(
                      t("history:added_detail", {
                        field: fieldLabel || obj.name,
                        detail,
                      })
                    );
                  }
                  return;
                }

                // ---------- REMOVED / DELETED ----------
                if (log.removed || log.deleted) {
                  const obj = log.removed || log.deleted;
                  const fieldLabel = getFieldLabel(obj.name);
                  const detail = obj.object;

                  if (!fieldLabel && !detail) {
                    msgList.push(t("history:removed"));
                  } else {
                    msgList.push(
                      t("history:removed_detail", {
                        field: fieldLabel || obj.name,
                        detail,
                      })
                    );
                  }
                  return;
                }

                // ---------- CHANGED ----------
                if (log.changed) {
                  const obj = log.changed;
                  if (Array.isArray(obj.fields)) {
                    const fields = obj.fields.map((f) => getFieldLabel(f)).join(", ");

                    msgList.push(
                      t("history:changed_fields", {
                        fields,
                      })
                    );
                    return;
                  }
                  if (obj.name && obj.object) {
                    msgList.push(buildChangedFromToMsg(obj.object, obj.name));
                    return;
                  }
                  msgList.push(
                    t("history:changed_detail", {
                      field: getFieldLabel(obj.name),
                      detail: JSON.stringify(obj),
                    })
                  );
                  return;
                }
                msgList.push(JSON.stringify(log));
              });
            }
          }

          return {
            ...item,
            key: item.id, // ใช้ id จริงจะดีกว่า Math.random()
            username: item.app_user?.username,
            messageList: msgList,
          };
        });

        setTableData((prev) => ({
          ...prev,
          loading: false,
          dataSource,
          pagination: { ...prev.pagination, current: page, pageSize: limit, total: resp.totalCount },
        }));
      } catch (err) {
        console.error("[BtnHistory] searchHistory: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        setTableData(defaultTableData);
      }
    },
    [object_id, content_type_id, t]
  );

  const onClick = useCallback(() => {
    setIsModalOpen(true);
    searchHistory({ page: 1, limit: 10 });
  }, [searchHistory]);

  const handleTableChange = useCallback(
    (current, pageSize) => {
      searchHistory({ page: current, limit: pageSize });
    },
    [searchHistory]
  );

  const onClose = useCallback(() => {
    setTableData(defaultTableData);
    setIsModalOpen(false);
  }, []);

  const tableProps = useMemo(
    () => ({
      ...tableData,
      pagination: {
        ...tableData.pagination,
        onChange: handleTableChange,
      },
    }),
    [tableData, handleTableChange]
  );

  return (
    <>
      <Button type="primary" onClick={onClick}>
        {t("BTN_History")}
      </Button>
      <ModalHistory title={title} open={isModalOpen} onClose={onClose} tableProps={tableProps} t={t} />
    </>
  );
};

export default BtnHistory;
